/**
 * Defines the browser-facing Box HTTP contract used by Vault.
 *
 * Provider response shapes and OAuth endpoints stay behind this service so UI
 * components work with typed remote entries and never construct credentialed
 * Box requests directly.
 */
export interface BoxEntry {
  id: string
  type: 'file' | 'folder' | 'web_link'
  name: string
  size?: number
  extension?: string
  modifiedAt?: string
}

interface BoxCollection<T> {
  entries: T[]
  next_marker?: string | null
}

type BoxApiEntry = Omit<BoxEntry, 'modifiedAt'> & { modified_at?: string }

const API = 'https://api.box.com/2.0'

const boxFetch = async (token: string, url: string, init: RequestInit = {}) => {
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  })
  if (!response.ok) {
    if (response.status === 401) throw new Error('Your Box connection expired. Connect again.')
    throw new Error(`Box request failed (${response.status})`)
  }
  return response
}

export const listBoxFolder = async (token: string, folderId: string) => {
  const entries: BoxEntry[] = []
  let marker: string | undefined
  do {
    const url = new URL(`${API}/folders/${encodeURIComponent(folderId)}/items`)
    url.searchParams.set('limit', '1000')
    url.searchParams.set('usemarker', 'true')
    url.searchParams.set('fields', 'id,type,name,size,extension,modified_at')
    if (marker) url.searchParams.set('marker', marker)
    const page = await boxFetch(token, url.toString()).then(
      response => response.json() as Promise<BoxCollection<BoxApiEntry>>,
    )
    entries.push(...page.entries.map(({ modified_at, ...entry }) => ({
      ...entry,
      modifiedAt: modified_at,
    })))
    marker = page.next_marker || undefined
  } while (marker)
  return entries
}
